import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
	Dialog,
	DialogContent,
	IconButton,
	CircularProgress,
	Typography
} from "@material-ui/core";
import { Close, History } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
	modalPapel,
	modalHero,
	modalHeroIcone,
	modalHeroEtiqueta,
	modalHeroTitulo,
	modalHeroSub,
	modalFechar,
	modalCorpo
} from "../../utils/estiloNavy";

// Traducao simples dos codigos do WhatsApp para quem le o painel.
const TIPO = code => {
	switch (Number(code)) {
		case 401:
			return { nome: "Desconectado pelo WhatsApp", grave: true };
		case 403:
			return { nome: "Número restrito", grave: true };
		case 440:
			return { nome: "Aberto em outro lugar", grave: true };
		case 408:
			return { nome: "Tempo esgotado (rede)", grave: false };
		case 428:
			return { nome: "Fechada pelo WhatsApp", grave: false };
		case 500:
			return { nome: "Erro do servidor do WhatsApp", grave: false };
		case 503:
			return { nome: "WhatsApp fora do ar", grave: false };
		default:
			return { nome: code ? `Código ${code}` : "Travou e foi reconectada", grave: false };
	}
};

const PERIODOS = [
	{ dias: 1, nome: "24 horas" },
	{ dias: 7, nome: "7 dias" },
	{ dias: 30, nome: "30 dias" }
];

const duracao = seg => {
	const s = Math.max(0, Number(seg) || 0);
	if (s < 60) return `${s}s`;
	if (s < 3600) return `${Math.round(s / 60)} min`;
	const h = Math.floor(s / 3600);
	const m = Math.round((s % 3600) / 60);
	return m ? `${h}h ${m}min` : `${h}h`;
};

const dataHora = iso => {
	const d = new Date(iso);
	return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const useStyles = makeStyles(theme => {
	const dark = theme.palette.type === "dark";
	return {
		paper: modalPapel(theme, 920),
		header: {
			...modalHero(theme),
			display: "flex",
			alignItems: "center",
			gap: 14,
			flexWrap: "wrap"
		},
		headerIcon: modalHeroIcone,
		etiqueta: modalHeroEtiqueta,
		titulo: modalHeroTitulo,
		sub: modalHeroSub,
		periodos: {
			marginLeft: "auto", display: "flex", gap: 4, padding: 4, borderRadius: 12,
			backgroundColor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.14)"
		},
		periodoBtn: {
			border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700,
			padding: "6px 12px", borderRadius: 9, background: "transparent", color: "#cbd5e1",
			transition: "background-color .15s, color .15s",
			"&:hover": { color: "#fff" },
			"&:focus": { outline: "none" }
		},
		periodoAtivo: {
			backgroundColor: "#fff", color: "#1E9855 !important",
			boxShadow: "0 2px 8px rgba(0,0,0,.25)"
		},
		fechar: modalFechar,
		conteudo: { ...modalCorpo(theme), display: "block", padding: "18px 22px !important" },
		cards: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 10, marginBottom: 18 },
		card: {
			position: "relative", overflow: "hidden",
			borderRadius: 16, padding: "12px 14px 12px 17px",
			border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)"}`,
			backgroundColor: dark ? "rgba(255,255,255,.04)" : "#fff",
			boxShadow: dark ? "none" : "0 6px 18px rgba(15,23,42,0.05)",
			animation: "iaSubir .35s ease backwards",
			"&::before": {
				content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
				background: "linear-gradient(180deg,#34d399,#1E9855)"
			}
		},
		cardNome: { fontSize: "0.86rem", fontWeight: 800, color: theme.palette.text.primary, marginBottom: 6 },
		cardLinha: { display: "flex", justifyContent: "space-between", fontSize: "0.76rem", color: theme.palette.text.secondary, marginTop: 3, "& b": { color: theme.palette.text.primary } },
		cardGrave: { "& b": { color: "#dc2626" } },
		secao: { fontSize: "0.72rem", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: theme.palette.text.secondary, margin: "4px 0 8px" },
		lista: { display: "flex", flexDirection: "column", gap: 6 },
		item: {
			display: "grid", gridTemplateColumns: "92px 1fr auto", alignItems: "center", gap: 12,
			padding: "10px 12px", borderRadius: 12, border: `1px solid ${theme.palette.divider}`,
			backgroundColor: dark ? "rgba(255,255,255,.03)" : "#fff",
			borderLeft: "4px solid #94a3b8"
		},
		itemGrave: { borderLeftColor: "#dc2626" },
		itemLeve: { borderLeftColor: "#22c55e" },
		itemAberto: { borderLeftColor: "#f59e0b" },
		hora: { fontSize: "0.76rem", fontWeight: 700, color: theme.palette.text.secondary },
		itemNome: { fontSize: "0.84rem", fontWeight: 700, color: theme.palette.text.primary },
		itemMotivo: { fontSize: "0.74rem", color: theme.palette.text.secondary },
		pill: { fontSize: "0.7rem", fontWeight: 800, padding: "3px 9px", borderRadius: 20, whiteSpace: "nowrap" },
		pillOk: { color: "#15803d", backgroundColor: "rgba(34,197,94,.15)" },
		pillQr: { color: "#b91c1c", backgroundColor: "rgba(220,38,38,.12)" },
		pillFora: { color: "#b45309", backgroundColor: "rgba(245,158,11,.15)" },
		vazio: { textAlign: "center", padding: "40px 0", color: theme.palette.text.secondary, fontSize: "0.88rem" },
		legenda: { fontSize: "0.74rem", color: theme.palette.text.secondary, marginTop: 14, lineHeight: 1.5 }
	};
});

const HistoricoQuedasModal = ({ open, onClose }) => {
	const classes = useStyles();
	const [dias, setDias] = useState(7);
	const [carregando, setCarregando] = useState(false);
	const [quedas, setQuedas] = useState([]);

	useEffect(() => {
		if (!open) return;
		let ativo = true;
		setCarregando(true);
		api
			.get("/conexao-quedas", { params: { dias } })
			.then(({ data }) => ativo && setQuedas(data.quedas || []))
			.catch(err => toastError(err))
			.finally(() => ativo && setCarregando(false));
		return () => {
			ativo = false;
		};
	}, [open, dias]);

	const resumo = useMemo(() => {
		const porConexao = {};
		quedas.forEach(q => {
			const r = (porConexao[q.whatsappId] = porConexao[q.whatsappId] || {
				nome: q.conexao, total: 0, qr: 0, maior: 0, ultima: q.caiuEm
			});
			r.total += 1;
			if (q.precisaQr) r.qr += 1;
			r.maior = Math.max(r.maior, Number(q.duracaoSeg) || 0);
		});
		return Object.values(porConexao).sort((a, b) => b.qr - a.qr || b.total - a.total);
	}, [quedas]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.paper }}>
			<div className={classes.header}>
				<div className={classes.headerIcon}><History /></div>
				<div style={{ minWidth: 0 }}>
					<div className={classes.etiqueta}>Conexões</div>
					<div className={classes.titulo}>Histórico de quedas</div>
					<div className={classes.sub}>Quando cada número caiu, por quê e quanto tempo ficou fora.</div>
				</div>
				<div className={classes.periodos}>
					{PERIODOS.map(p => (
						<button
							key={p.dias}
							type="button"
							className={`${classes.periodoBtn} ${dias === p.dias ? classes.periodoAtivo : ""}`}
							onClick={() => setDias(p.dias)}
						>
							{p.nome}
						</button>
					))}
				</div>
				<IconButton size="small" onClick={onClose} className={classes.fechar}><Close fontSize="small" /></IconButton>
			</div>

			<DialogContent className={classes.conteudo}>
				{carregando ? (
					<div className={classes.vazio}><CircularProgress size={26} /></div>
				) : quedas.length === 0 ? (
					<div className={classes.vazio}>Nenhuma queda registrada neste período 🎉</div>
				) : (
					<>
						<div className={classes.cards}>
							{resumo.map(r => (
								<div key={r.nome} className={classes.card}>
									<div className={classes.cardNome}>{r.nome}</div>
									<div className={classes.cardLinha}><span>Quedas</span><b>{r.total}</b></div>
									<div className={`${classes.cardLinha} ${r.qr ? classes.cardGrave : ""}`}>
										<span>Precisou ler QR</span><b>{r.qr}</b>
									</div>
									<div className={classes.cardLinha}><span>Maior tempo fora</span><b>{duracao(r.maior)}</b></div>
									<div className={classes.cardLinha}><span>Última</span><b>{dataHora(r.ultima)}</b></div>
								</div>
							))}
						</div>

						<div className={classes.secao}>Linha do tempo</div>
						<div className={classes.lista}>
							{quedas.map(q => {
								const tipo = TIPO(q.statusCode);
								const aberta = !q.voltouEm;
								const precisaQr = !!q.precisaQr;
								const corItem = precisaQr || tipo.grave
									? classes.itemGrave
									: aberta ? classes.itemAberto : classes.itemLeve;
								let pill;
								if (aberta && precisaQr) pill = <span className={`${classes.pill} ${classes.pillQr}`}>Aguardando QR · {duracao(q.duracaoSeg)}</span>;
								else if (aberta) pill = <span className={`${classes.pill} ${classes.pillFora}`}>Fora há {duracao(q.duracaoSeg)}</span>;
								else if (precisaQr) pill = <span className={`${classes.pill} ${classes.pillQr}`}>Voltou com QR · {duracao(q.duracaoSeg)}</span>;
								else pill = <span className={`${classes.pill} ${classes.pillOk}`}>Voltou sozinha em {duracao(q.duracaoSeg)}</span>;
								return (
									<div key={q.id} className={`${classes.item} ${corItem}`}>
										<span className={classes.hora}>{dataHora(q.caiuEm)}</span>
										<div>
											<div className={classes.itemNome}>{q.conexao} — {tipo.nome}</div>
											<div className={classes.itemMotivo}>{q.motivo}</div>
										</div>
										{pill}
									</div>
								);
							})}
						</div>
					</>
				)}
				<Typography className={classes.legenda} component="div">
					<b>Verde</b>: caiu e voltou sozinha (normal, não precisa fazer nada). <b>Laranja</b>: ainda reconectando.
					{" "}<b>Vermelho</b>: o WhatsApp desconectou o número — precisa ler o QR. Muitas quedas vermelhas no mesmo
					número indicam que o WhatsApp está restringindo ele.
				</Typography>
			</DialogContent>
		</Dialog>
	);
};

export default HistoricoQuedasModal;
